import React, { useState, useEffect } from 'react';
import { useSearchParams, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import ProductList from './components/ProductList';
import Cart from './components/Cart';
import OrderForm from './components/OrderForm';
import Header from './components/Header';
import AdminPanel from './components/AdminPanel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function Catalog() {
  const [searchParams] = useSearchParams();
  const catalogId = searchParams.get('catalog_id') || process.env.REACT_APP_DEFAULT_CATALOG_ID;
  
  // Получаем user_id из разных источников:
  // 1. Из WebAppData в URL hash (MAX передает данные через hash)
  // 2. Из window.WebApp.initDataUnsafe (MAX WebApp API)
  // 3. Из window.Telegram.WebApp (альтернативный способ)
  // 4. Из URL параметров
  const getUserId = () => {
    let debugInfo = [];
    
    // Пробуем получить из WebAppData в hash (MAX передает данные через #WebAppData=...)
    try {
      const hash = window.location.hash;
      if (hash.includes('WebAppData=')) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const webAppData = hashParams.get('WebAppData');
        if (webAppData) {
          // WebAppData содержит URL-encoded строку с параметрами
          // Формат: user=%7B%22id%22%3A160345307%2C... (URL-encoded JSON)
          const webAppParams = new URLSearchParams(decodeURIComponent(webAppData));
          const userData = webAppParams.get('user');
          if (userData) {
            try {
              const user = JSON.parse(userData);
              if (user && user.id) {
                debugInfo.push(`✅ user_id из WebAppData (hash): ${user.id}`);
                return { userId: String(user.id), debugInfo };
              }
            } catch (e) {
              debugInfo.push(`⚠️ Ошибка парсинга user из WebAppData: ${e.message}`);
            }
          }
        }
      }
    } catch (e) {
      debugInfo.push(`⚠️ Ошибка обработки WebAppData: ${e.message}`);
    }
    
    // Пробуем получить из MAX WebApp API (основной способ)
    if (window.WebApp && window.WebApp.initDataUnsafe && window.WebApp.initDataUnsafe.user) {
      const maxUserId = window.WebApp.initDataUnsafe.user.id;
      if (maxUserId) {
        debugInfo.push(`✅ user_id из WebApp: ${maxUserId}`);
        return { userId: String(maxUserId), debugInfo };
      }
    }
    
    // Пробуем альтернативный способ (Telegram WebApp API, который может использоваться MAX)
    if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe && window.Telegram.WebApp.initDataUnsafe.user) {
      const tgUserId = window.Telegram.WebApp.initDataUnsafe.user.id;
      if (tgUserId) {
        debugInfo.push(`✅ user_id из Telegram.WebApp: ${tgUserId}`);
        return { userId: String(tgUserId), debugInfo };
      }
    }
    
    // Пробуем получить из URL параметров
    const urlUserId = searchParams.get('user_id') || 
                     new URLSearchParams(window.location.search).get('user_id') ||
                     new URLSearchParams(window.location.hash.split('?')[1] || '').get('user_id');
    
    if (urlUserId) {
      debugInfo.push(`✅ user_id из URL: ${urlUserId}`);
      return { userId: urlUserId, debugInfo };
    }
    
    // Пробуем получить из всех возможных источников
    debugInfo.push(`⚠️ user_id не найден`);
    debugInfo.push(`🔍 window.WebApp: ${window.WebApp ? 'есть' : 'нет'}`);
    debugInfo.push(`🔍 window.Telegram: ${window.Telegram ? 'есть' : 'нет'}`);
    debugInfo.push(`🔍 Hash содержит WebAppData: ${window.location.hash.includes('WebAppData') ? 'да' : 'нет'}`);
    
    if (window.location.hash.includes('WebAppData')) {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const webAppData = hashParams.get('WebAppData');
        if (webAppData) {
          debugInfo.push(`🔍 WebAppData найден в hash`);
          const webAppParams = new URLSearchParams(decodeURIComponent(webAppData));
          const userData = webAppParams.get('user');
          if (userData) {
            debugInfo.push(`🔍 user данные найдены в WebAppData`);
            try {
              const user = JSON.parse(userData);
              debugInfo.push(`🔍 user.id: ${user.id || 'не найден'}`);
            } catch (e) {
              debugInfo.push(`⚠️ Ошибка парсинга user: ${e.message}`);
            }
          } else {
            debugInfo.push(`⚠️ user не найден в WebAppData`);
          }
        }
      } catch (e) {
        debugInfo.push(`⚠️ Ошибка обработки hash: ${e.message}`);
      }
    }
    
    return { userId: null, debugInfo };
  };
  
  const { userId, debugInfo: initialDebugInfo } = getUserId();
  const [debugInfo] = useState(initialDebugInfo);
  
  const [catalog, setCatalog] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    console.log('App mounted, catalogId:', catalogId);
    console.log('API_URL:', API_URL);
    
    if (!catalogId) {
      setError('Не указан идентификатор каталога. Добавьте ?catalog_id=YOUR_CATALOG_ID к URL или настройте REACT_APP_DEFAULT_CATALOG_ID');
      setLoading(false);
      return;
    }

    // Проверяем существование каталога
    console.log('Fetching catalog:', `${API_URL}/catalog/${catalogId}`);
    axios.get(`${API_URL}/catalog/${catalogId}`)
      .then((response) => {
        console.log('Catalog found:', response.data);
        // Сохраняем данные каталога
        setCatalog(response.data);
        // Загружаем товары
        return axios.get(`${API_URL}/catalog/${catalogId}/products`);
      })
      .then(response => {
        console.log('Products loaded:', response.data);
        setProducts(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading catalog:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          url: err.config?.url
        });
        setError(`Каталог не найден или произошла ошибка: ${err.message}`);
        setLoading(false);
      });

    // Загружаем корзину из localStorage
    const savedCart = localStorage.getItem(`cart_${catalogId}`);
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart from localStorage:', e);
      }
    }
  }, [catalogId]);

  // Сохраняем корзину в localStorage
  useEffect(() => {
    if (catalogId) {
      localStorage.setItem(`cart_${catalogId}`, JSON.stringify(cart));
    }
  }, [cart, catalogId]);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item =>
        item.id === productId
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleOrderSubmit = async (orderData) => {
    try {
      const orderItems = cart.map(item => ({
        product_id: item.id,
        quantity: item.quantity,
        price: item.price
      }));

      // Используем userId, полученный из MAX WebApp API или URL
      console.log('🔍 OrderForm: Using user_id:', userId);
      console.log('🔍 OrderForm: window.WebApp:', window.WebApp);

      const response = await axios.post(`${API_URL}/orders`, {
        catalog_id: catalogId,
        customer_name: orderData.name,
        customer_phone: orderData.phone,
        address: orderData.address,
        comment: orderData.comment,
        items: orderItems,
        user_id: userId || null
      });

      // Очищаем корзину
      setCart([]);
      localStorage.removeItem(`cart_${catalogId}`);
      
      // Закрываем форму и показываем успех
      setShowOrderForm(false);
      setShowCart(false);
      
      alert(`Заказ оформлен! Номер заказа: ${response.data.order_id.substring(0, 8)}`);
      
      // Здесь можно отправить сообщение боту MAX через API
      // await sendOrderToBot(response.data.order_id);
      
    } catch (err) {
      console.error('Error creating order:', err);
      alert('Ошибка при оформлении заказа. Попробуйте еще раз.');
    }
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading">Загрузка каталога...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="error">
          <h2>Ошибка</h2>
          <p>{error}</p>
          {!catalogId && (
            <div style={{ marginTop: '20px', padding: '20px', background: '#f0f0f0', borderRadius: '8px' }}>
              <p><strong>Как использовать:</strong></p>
              <p>Добавьте параметр catalog_id к URL:</p>
              <code style={{ display: 'block', padding: '10px', background: '#fff', marginTop: '10px' }}>
                http://localhost:3000?catalog_id=ВАШ_CATALOG_ID
              </code>
              <p style={{ marginTop: '10px' }}>
                Для получения catalog_id запустите: <code>node server/seed.js</code>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Визуальная отладочная информация о user_id - скрыта в продакшене */}
      {process.env.NODE_ENV === 'development' && !userId && (
        <div className="debug-block" style={{
          padding: '10px',
          margin: '10px',
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '5px',
          fontSize: '12px'
        }}>
          <strong>⚠️ Отладка:</strong> user_id не найден
          <button 
            onClick={() => setShowDebug(!showDebug)}
            style={{ marginLeft: '10px', padding: '2px 8px', fontSize: '11px' }}
          >
            {showDebug ? 'Скрыть' : 'Показать'} детали
          </button>
          {showDebug && (
            <div style={{ marginTop: '10px', padding: '10px', background: '#fff', borderRadius: '3px' }}>
              {debugInfo.map((info, idx) => (
                <div key={idx} style={{ marginBottom: '5px' }}>{info}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {process.env.NODE_ENV === 'development' && userId && (
        <div className="debug-block" style={{
          padding: '10px',
          margin: '10px',
          background: '#d4edda',
          border: '1px solid #28a745',
          borderRadius: '5px',
          fontSize: '12px'
        }}>
          <strong>✅ user_id найден:</strong> {userId}
        </div>
      )}
      
      <Header 
        cartCount={cart.reduce((sum, item) => sum + item.quantity, 0)}
        onCartClick={() => setShowCart(true)}
        catalogName={catalog?.name || 'Каталог товаров'}
      />
      
      {showCart ? (
        <Cart
          cart={cart}
          onUpdateQuantity={updateQuantity}
          onRemove={removeFromCart}
          onOrder={() => {
            setShowOrderForm(true);
            setShowCart(false);
          }}
          onClose={() => setShowCart(false)}
          totalPrice={getTotalPrice()}
        />
      ) : showOrderForm ? (
        <OrderForm
          onSubmit={handleOrderSubmit}
          onCancel={() => setShowOrderForm(false)}
          catalogId={catalogId}
        />
      ) : (
        <ProductList 
          products={products} 
          onAddToCart={addToCart}
          cart={cart}
          onUpdateQuantity={updateQuantity}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/" element={<Catalog />} />
    </Routes>
  );
}

export default App;

